import { Box, Typography, Button, useTheme, CircularProgress, Link } from '@mui/material';
import { ReactNode } from 'react';
import { alpha } from '@mui/material/styles';
import { useRef, useState, useCallback } from 'react';
import Header from '@/Components/MagicLink/Header';
import Section from '@/Components/MagicLink/Section';
import { useMagicLinkAppProvider, MagicLinkView, getToken } from '@/Provider/MagicLinkAppProvider';
import { useIdCardUpload, type UploadIdCardOutput, CardDocumentIcon } from 'request-form-lib';
import MagicLinkActionBar from '@/Components/MagicLink/MagicLinkActionBar';
import BackActionButton from '@/Components/MagicLink/BackActionButton';
import { uploadIdCardWithValidation } from '@/Api/MagicLinkApi';
import { Theme } from '@mui/material/styles';
import IdCardBadge from '@/Components/MagicLink/IdCardBadge';
import { getCenterStyles } from 'vig-ui-library';
import {
    magicLinkPageColumnSx,
    magicLinkPageOuterSx,
} from '@/Utils/MagicLink/pageLayout';

interface IdCardUploadContainerProps {
    label: string;
    isDragActive: boolean;
    isUploading: boolean;
    handleUploadClick: () => void;
    theme: Theme;
    isFileUploaded?: boolean;
    onUploadAgain?: () => void;
    onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragEnter?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragLeave?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
    children?: ReactNode;
}

/**
 * White Section card containing the dashed drop zone for one ID-card side.
 *
 * Visual chrome (white card + 24px header + dashed drop area) mirrors the
 * `TaxonomyUploadZone` and `UploadIdCardView` patterns from
 * `RequestFormLib`, so the public Magic Link app feels consistent with the
 * in-app request form.
 */
const IdCardUploadContainer = ({
    label,
    isDragActive,
    isUploading,
    handleUploadClick,
    theme,
    isFileUploaded = false,
    onUploadAgain,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
    children,
}: IdCardUploadContainerProps) => {
    return (
        <Section
            title={label}
            headerRight={
                isFileUploaded && onUploadAgain ? (
                    <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                            e.preventDefault();
                            onUploadAgain();
                        }}
                        sx={{
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        Nahrať znova
                    </Link>
                ) : undefined
            }
        >
            {children ? (
                children
            ) : (
                <Box sx={{ position: 'relative', width: '100%' }}>
                    <Box
                        onClick={isUploading ? undefined : handleUploadClick}
                        onDragOver={onDragOver}
                        onDragEnter={onDragEnter}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                        role="button"
                        tabIndex={0}
                        aria-label={`Nahrať súbor: ${label}`}
                        sx={{
                            ...getCenterStyles('column'),
                            width: '100%',
                            flexShrink: 0,
                            minHeight: 188,
                            boxSizing: 'border-box',
                            padding: '20px',
                            borderRadius: '7px',
                            border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                            backgroundColor: isDragActive
                                ? alpha(theme.palette.primary.main, 0.06)
                                : 'transparent',
                            transition: 'border-color 0.2s ease, background-color 0.2s ease',
                            cursor: isUploading ? 'not-allowed' : 'pointer',
                            opacity: isUploading ? 0.7 : 1,
                            '&:hover': isUploading
                                ? {}
                                : {
                                    borderColor: theme.palette.primary.main,
                                },
                            '&:focus-visible': {
                                outline: 'none',
                                boxShadow: '0 0 0 2px rgba(0,0,0,0.1)',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                mb: 0.5,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.25,
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                component="p"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: theme.typography.pxToRem(16),
                                    lineHeight: theme.typography.pxToRem(24),
                                    textAlign: 'center',
                                }}
                            >
                                Nahrať
                            </Typography>
                            <Typography
                                variant="body2"
                                component="p"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: theme.typography.pxToRem(13),
                                    lineHeight: '132%',
                                    letterSpacing: 0,
                                    textAlign: 'center',
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Nahrajte súbor jpg, jpeg alebo png v maximálnej veľkosti 5MB
                            </Typography>
                        </Box>

                        <Box sx={{ ...getCenterStyles() }}>
                            <CardDocumentIcon width={48} height={48} color={theme.palette.primary.main} />
                        </Box>
                    </Box>

                    {isUploading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                borderRadius: '7px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress size={48} />
                            <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 'medium' }}>
                                Nahrávanie ID karty...
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Prosím počkajte, kým sa dokončí nahrávanie a overenie
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Section>
    );
};

const UploadIdCardView = () => {
    const theme = useTheme();
    const { navigateToView, magicLinkData, getIdCardFrontFileId, getIdCardBackFileId, setFrontSideUploadResult, setBackSideUploadResult } = useMagicLinkAppProvider();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragActiveFront, setIsDragActiveFront] = useState(false);
    const [isDragActiveBack, setIsDragActiveBack] = useState(false);
    const [frontFileId, setFrontFileId] = useState<string | null>(getIdCardFrontFileId());
    const [backFileId, setBackFileId] = useState<string | null>(getIdCardBackFileId());

    const requestId = magicLinkData?.linkData?.requestId;
    const frontFileUploaded = magicLinkData?.idCardFrontUploaded ?? false;
    const backFileUploaded = magicLinkData?.idCardBackUploaded ?? false;
    const bothSidesUploaded = frontFileUploaded && backFileUploaded;

    const linkTokenHash = getToken();

    const getImageUrl = (requestFileId: string | null | undefined): string | null => {
        if (!linkTokenHash || !requestFileId) return null;
        return `/api/magic-link/public/streamIdCardSideFile?linkTokenHash=${encodeURIComponent(linkTokenHash)}&requestFileId=${requestFileId}`;
    };

    const { isUploading, uploadError, uploadFiles, clearError } = useIdCardUpload({
        requestId: requestId || '',
        linkTokenHash: linkTokenHash!,
        uploadService: uploadIdCardWithValidation,
        onUploadSuccess: (result: UploadIdCardOutput) => {
            if (result.frontFileId) {
                setFrontFileId(result.frontFileId);
                setFrontSideUploadResult(result);
            }
            if (result.backFileId) {
                setBackFileId(result.backFileId);
                setBackSideUploadResult(result);
            }
        },
        onUploadError: (error: string) => {
            console.error('Upload error:', error);
        },
        onUploadComplete: () => {
            // Upload completed - could reload data here if needed
        },
    });

    const processFiles = useCallback((files: File[]) => {
        if (!files || files.length === 0) return;

        if (!requestId) {
            clearError();
            return;
        }

        const validFiles = Array.from(files).filter(file => {
            if (!file.type.startsWith('image/')) {
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) {
            clearError();
            return;
        }

        // Limit to 2 files (front and back)
        const filesToUpload = validFiles.slice(0, 2);

        uploadFiles(filesToUpload);
    }, [uploadFiles, clearError, requestId]);

    const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) return;

        processFiles(Array.from(files));

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [processFiles]);

    const handleUploadClick = useCallback(() => {
        if (isUploading) return;
        fileInputRef.current?.click();
    }, [isUploading]);

    const handleContinueClick = useCallback(() => {
        if (frontFileId && backFileId) {
            navigateToView(MagicLinkView.ID_CARD_UPLOADED);
        }
    }, [frontFileId, backFileId, navigateToView]);

    const handleUploadAgainFront = useCallback(() => {
        setFrontFileId(null);
    }, []);

    const handleUploadAgainBack = useCallback(() => {
        setBackFileId(null);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>, side: 'front' | 'back') => {
        e.preventDefault();
        e.stopPropagation();
        if (!isUploading) {
            if (side === 'front') {
                setIsDragActiveFront(true);
            } else {
                setIsDragActiveBack(true);
            }
        }
    }, [isUploading]);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>, side: 'front' | 'back') => {
        e.preventDefault();
        e.stopPropagation();
        const currentTarget = e.currentTarget;
        const relatedTarget = e.relatedTarget as Node | null;
        if (!currentTarget.contains(relatedTarget)) {
            if (side === 'front') {
                setIsDragActiveFront(false);
            } else {
                setIsDragActiveBack(false);
            }
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragActiveFront(false);
        setIsDragActiveBack(false);

        if (isUploading) return;

        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) {
            processFiles(droppedFiles);
        }
    }, [isUploading, processFiles]);

    const bothSidesUploadedByIds = (frontFileId !== null && backFileId !== null) || bothSidesUploaded;

    return (
        <Box component="main" sx={magicLinkPageOuterSx}>
            <Box sx={{ ...magicLinkPageColumnSx, gap: 3 }}>
                <Header />

                <Box
                    sx={{
                        width: '100%',
                        textAlign: { xs: 'center', md: 'left' },
                    }}
                >
                    <Typography variant="h5" component="h1" gutterBottom>
                        Nahrajte, prosím, Váš občiansky preukaz
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            maxWidth: { md: 560 },
                            mx: { xs: 'auto', md: 0 },
                        }}
                    >
                        Pre overenie Vašej identity, prosím, nahrajte fotografiu prednej a zadnej strany Vášho občianskeho preukazu.
                    </Typography>
                </Box>

                {/* ------------------------------------------------------------- */}
                {/* Upload sections — one Section card per ID-card side */}
                {/* ------------------------------------------------------------- */}
                {!bothSidesUploaded && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            width: '100%',
                        }}
                    >
                        <IdCardUploadContainer
                            label="Predná strana"
                            isDragActive={isDragActiveFront}
                            isUploading={isUploading}
                            handleUploadClick={handleUploadClick}
                            theme={theme}
                            isFileUploaded={!!(frontFileId && getImageUrl(frontFileId))}
                            onUploadAgain={handleUploadAgainFront}
                            onDragOver={handleDragOver}
                            onDragEnter={(e) => handleDragEnter(e, 'front')}
                            onDragLeave={(e) => handleDragLeave(e, 'front')}
                            onDrop={handleDrop}
                        >
                            {frontFileId && getImageUrl(frontFileId) ? (
                                <IdCardBadge
                                    label="Predná strana OP"
                                    imageUrl={getImageUrl(frontFileId)}
                                    fileId={frontFileId}
                                    fileName="ID Card Front"
                                    linkTokenHash={linkTokenHash}
                                />
                            ) : null}
                        </IdCardUploadContainer>

                        <IdCardUploadContainer
                            label="Zadná strana"
                            isDragActive={isDragActiveBack}
                            isUploading={isUploading}
                            handleUploadClick={handleUploadClick}
                            theme={theme}
                            isFileUploaded={!!(backFileId && getImageUrl(backFileId))}
                            onUploadAgain={handleUploadAgainBack}
                            onDragOver={handleDragOver}
                            onDragEnter={(e) => handleDragEnter(e, 'back')}
                            onDragLeave={(e) => handleDragLeave(e, 'back')}
                            onDrop={handleDrop}
                        >
                            {backFileId && getImageUrl(backFileId) ? (
                                <IdCardBadge
                                    label="Zadná strana OP"
                                    imageUrl={getImageUrl(backFileId)}
                                    fileId={backFileId}
                                    fileName="ID Card Back"
                                    linkTokenHash={linkTokenHash}
                                />
                            ) : null}
                        </IdCardUploadContainer>

                        {uploadError && (
                            <Box
                                sx={{
                                    padding: 2,
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    borderRadius: '7px',
                                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                }}
                            >
                                <Typography variant="body2" color="error" sx={{ fontWeight: 'medium' }}>
                                    Chyba pri nahrávaní
                                </Typography>
                                <Typography variant="body2" color="error">
                                    {uploadError}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                <MagicLinkActionBar
                    secondary={
                        <BackActionButton onClick={() => navigateToView(MagicLinkView.INTRO)} />
                    }
                    primary={
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleContinueClick}
                            disabled={!bothSidesUploadedByIds || isUploading}
                            sx={{
                                textTransform: 'none',
                                height: '55px',
                                fontWeight: 500,
                            }}
                        >
                            Pokračovať
                        </Button>
                    }
                />

                {/* Hidden file input — accepts multiple, max 2 (front + back). */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
            </Box>
        </Box>
    );
};

export default UploadIdCardView;
